import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MessageSquare,
  HelpCircle,
  BarChart3,
  Hand,
  Pin,
  CheckCircle2,
  ArrowUp,
  Users,
  Send,
  AlertTriangle,
  WifiOff,
} from 'lucide-react';
import type {
  LiveClassMessage,
  PollResults,
  PresenceUser,
  WsServerEnvelope,
} from '@edytech/shared/types';
import { WS_MAX_MESSAGE_LENGTH } from '@edytech/shared/constants';
import { useLiveRoom } from '../../hooks/useLiveRoom';
import { liveClassService } from '../../services/live-class.service';

/**
 * Phase 3 — in-room Chat / Q&A / Polls panel (TRD §4.3/§6.5).
 * Live message list (windowed + `?after=` resync on reconnect), Q&A upvoting /
 * answering, poll cards with live results, moderator actions (pin/hide/answer)
 * and the presence sidebar. Works over WS or, in degraded mode, transparently
 * over the REST fallback with no feature loss.
 */

type Tab = 'chat' | 'qa' | 'polls';

interface LiveRoomPanelProps {
  sessionId: number;
  canModerate: boolean;
  /** The signed-in user's id (used to mark "you" in presence). */
  currentUserId?: number;
}

function mergeById(prev: LiveClassMessage[], next: LiveClassMessage[]): LiveClassMessage[] {
  const map = new Map<number, LiveClassMessage>();
  for (const m of prev) map.set(m.id, m);
  for (const m of next) map.set(m.id, m);
  return [...map.values()].sort((a, b) => a.id - b.id);
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export const LiveRoomPanel: React.FC<LiveRoomPanelProps> = ({
  sessionId,
  canModerate,
  currentUserId,
}) => {
  const [tab, setTab] = useState<Tab>('chat');
  const [messages, setMessages] = useState<LiveClassMessage[]>([]);
  const [questions, setQuestions] = useState<LiveClassMessage[]>([]);
  const [polls, setPolls] = useState<LiveClassMessage[]>([]);
  const [pollResults, setPollResults] = useState<Record<number, PollResults>>({});
  const [presence, setPresence] = useState<PresenceUser[]>([]);
  const [composer, setComposer] = useState('');
  const [handRaised, setHandRaised] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [showPollForm, setShowPollForm] = useState(false);
  const lastIdRef = useRef<number | null>(null);
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showBanner = useCallback((msg: string) => {
    setBanner(msg);
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setBanner(null), 4000);
  }, []);

  /* ------------------------------ envelope handling ------------------------------ */

  const handleEnvelope = useCallback(
    (env: WsServerEnvelope) => {
      switch (env.type) {
        case 'presence.update':
          setPresence(env.users);
          break;
        case 'chat.message':
        case 'announcement':
          setMessages((prev) => mergeById(prev, [env.message]));
          break;
        case 'qa.question':
          setMessages((prev) => mergeById(prev, [env.message]));
          setQuestions((prev) => mergeById(prev, [env.message]));
          break;
        case 'qa.answered':
          setMessages((prev) => prev.map((m) => (m.id === env.messageId ? { ...m, isAnswered: true } : m)));
          setQuestions((prev) => prev.map((m) => (m.id === env.messageId ? { ...m, isAnswered: true } : m)));
          break;
        case 'qa.votes':
          setMessages((prev) => prev.map((m) => (m.id === env.messageId ? { ...m, votes: env.votes } : m)));
          setQuestions((prev) => prev.map((m) => (m.id === env.messageId ? { ...m, votes: env.votes } : m)));
          break;
        case 'poll.created':
          setMessages((prev) => mergeById(prev, [env.poll]));
          setPolls((prev) => mergeById(prev, [env.poll]));
          break;
        case 'poll.results':
          setPollResults((prev) => ({ ...prev, [env.pollId]: env.results }));
          break;
        case 'moderation.recalled':
          setMessages((prev) => prev.filter((m) => m.id !== env.messageId));
          setQuestions((prev) => prev.filter((m) => m.id !== env.messageId));
          setPolls((prev) => prev.filter((m) => m.id !== env.messageId));
          break;
        case 'moderation.pinned':
          setMessages((prev) => prev.map((m) => (m.id === env.messageId ? { ...m, isPinned: env.isPinned } : m)));
          setQuestions((prev) => prev.map((m) => (m.id === env.messageId ? { ...m, isPinned: env.isPinned } : m)));
          break;
        case 'error':
          showBanner(env.message);
          break;
        default:
          break;
      }
    },
    [showBanner]
  );

  /* ------------------------------ history + resync ------------------------------ */

  const loadHistory = useCallback(
    async (after: number | null) => {
      try {
        const page = await liveClassService.getMessages(sessionId, {
          after: after ?? undefined,
          limit: 200,
        });
        setMessages((prev) =>
          mergeById(
            prev,
            page.messages.filter((m) => m.messageType === 'CHAT' || m.messageType === 'ANNOUNCEMENT')
          )
        );
        setQuestions((prev) => mergeById(prev, page.messages.filter((m) => m.messageType === 'QUESTION')));
        setPolls((prev) => mergeById(prev, page.messages.filter((m) => m.messageType === 'POLL')));
        if (page.messages.length > 0) {
          lastIdRef.current = page.messages[page.messages.length - 1].id;
        }
      } catch {
        showBanner('Could not load message history.');
      }
    },
    [sessionId, showBanner]
  );

  const room = useLiveRoom({
    sessionId,
    enabled: true,
    onEnvelope: handleEnvelope,
    onResync: () => void loadHistory(lastIdRef.current),
  });

  useEffect(() => {
    if (!sessionId) return;
    // Initial history load — async setState after the awaited fetch (not a sync render cascade).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadHistory(null);
  }, [sessionId, loadHistory]);

  /* ------------------------------ actions ------------------------------ */

  const sendComposer = useCallback(() => {
    const content = composer.trim();
    if (!content) return;
    if (content.length > WS_MAX_MESSAGE_LENGTH) {
      showBanner(`Messages are limited to ${WS_MAX_MESSAGE_LENGTH} characters.`);
      return;
    }
    if (tab === 'qa') {
      room.send({ type: 'qa.ask', content });
    } else {
      room.send({ type: 'chat.send', content });
    }
    setComposer('');
  }, [composer, tab, room, showBanner]);

  const toggleRaiseHand = useCallback(() => {
    const next = !handRaised;
    setHandRaised(next);
    room.send({ type: 'raise.hand', on: next });
  }, [handRaised, room]);

  const upvote = useCallback((messageId: number) => {
    room.send({ type: 'qa.vote', messageId });
  }, [room]);

  const moderate = useCallback(
    (messageId: number, action: 'pin' | 'hide' | 'answer', pinned?: boolean) => {
      if (!canModerate) return;
      if (action === 'pin') room.send({ type: 'moderation.pin', messageId, pinned: pinned ?? true });
      if (action === 'hide') room.send({ type: 'moderation.hide', messageId });
      if (action === 'answer') room.send({ type: 'moderation.answer', messageId });
    },
    [canModerate, room]
  );

  const votePoll = useCallback(
    (pollId: number, optionId: string) => {
      room.send({ type: 'poll.vote', pollId, optionId });
    },
    [room]
  );

  const createPoll = useCallback(
    async (question: string, optionsText: string) => {
      const options = optionsText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      if (question.trim().length === 0 || options.length < 2) {
        showBanner('A poll needs a question and at least 2 options (one per line).');
        return;
      }
      try {
        await liveClassService.createPoll(sessionId, {
          question: question.trim(),
          options,
          allowMultiple: false,
        });
        setShowPollForm(false);
      } catch (e) {
        showBanner(e instanceof Error ? e.message : 'Could not create poll.');
      }
    },
    [sessionId, showBanner]
  );

  /* ------------------------------ render helpers ------------------------------ */

  const resultsFor = (poll: LiveClassMessage): PollResults | null => pollResults[poll.id] ?? null;

  const presenceCount = presence.length;
  const qaCount = questions.length;
  const pollCount = polls.length;

  const statusLabel =
    room.status === 'open'
      ? 'Live'
      : room.status === 'degraded'
        ? 'Degraded (REST)'
        : room.status === 'reconnecting'
          ? 'Reconnecting…'
          : room.status === 'connecting'
            ? 'Connecting…'
            : 'Offline';

  /* ------------------------------ message rows ------------------------------ */

  const chatRows = messages.map((m) => (
    <div key={m.id} className="rounded-lg bg-slate-800/60 p-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold text-slate-200">{m.userName ?? 'System'}</span>
        <span className="flex items-center gap-1 text-[10px] text-slate-500">
          {m.isPinned && <Pin className="h-3 w-3 text-amber-400" />}
          {formatTime(m.createdAt)}
        </span>
      </div>
      <p className="mt-0.5 text-sm text-slate-100">{m.content}</p>
      {canModerate && (
        <div className="mt-1 flex gap-2">
          <button
            onClick={() => moderate(m.id, 'pin', !m.isPinned)}
            className="text-[10px] text-slate-400 hover:text-amber-300"
            title={m.isPinned ? 'Unpin' : 'Pin'}
          >
            {m.isPinned ? 'Unpin' : 'Pin'}
          </button>
          <button
            onClick={() => moderate(m.id, 'hide')}
            className="text-[10px] text-slate-400 hover:text-red-300"
            title="Hide"
          >
            Hide
          </button>
        </div>
      )}
    </div>
  ));

  const qaRows = questions.map((q) => {
    const results = resultsFor(q);
    return (
      <div key={q.id} className={`rounded-lg bg-slate-800/60 p-2 ${q.isPinned ? 'ring-1 ring-amber-400/50' : ''}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1 text-[10px] text-slate-500">
              <span className="font-semibold text-slate-300">{q.userName ?? 'Anonymous'}</span>
              {q.isPinned && <Pin className="h-3 w-3 text-amber-400" />}
              {q.isAnswered && (
                <span className="flex items-center gap-0.5 rounded bg-emerald-500/20 px-1 py-0.5 text-emerald-300">
                  <CheckCircle2 className="h-3 w-3" /> Answered
                </span>
              )}
            </div>
            <p className="mt-0.5 text-sm text-slate-100">{q.content}</p>
          </div>
          <button
            onClick={() => upvote(q.id)}
            className={`flex shrink-0 flex-col items-center rounded-md px-2 py-1 text-xs ${
              q.votes > 0 ? 'bg-indigo-500/30 text-indigo-200' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
            title="Upvote"
          >
            <ArrowUp className="h-3 w-3" />
            {q.votes}
          </button>
        </div>
        <div className="mt-2 text-[10px] text-slate-500">
          {results ? `${results.totalVotes} vote${results.totalVotes === 1 ? '' : 's'}` : `${q.votes} upvote${q.votes === 1 ? '' : 's'}`}
        </div>
        {canModerate && (
          <div className="mt-1 flex gap-2">
            <button onClick={() => moderate(q.id, 'answer')} className="text-[10px] text-emerald-300 hover:text-emerald-200">
              {q.isAnswered ? 'Answered ✓' : 'Mark answered'}
            </button>
            <button onClick={() => moderate(q.id, 'pin', !q.isPinned)} className="text-[10px] text-slate-400 hover:text-amber-300">
              {q.isPinned ? 'Unpin' : 'Pin'}
            </button>
            <button onClick={() => moderate(q.id, 'hide')} className="text-[10px] text-slate-400 hover:text-red-300">
              Hide
            </button>
          </div>
        )}
      </div>
    );
  });

  const pollCards = polls.map((poll) => {
    const results = resultsFor(poll);
    const total = results?.totalVotes ?? 0;
    const options = poll.poll?.options ?? [];
    return (
      <div key={poll.id} className="rounded-lg bg-slate-800/60 p-3">
        <div className="flex items-center gap-1 text-xs font-semibold text-slate-100">
          <BarChart3 className="h-3.5 w-3.5 text-indigo-300" />
          {poll.content}
        </div>
        <div className="mt-2 space-y-1.5">
          {options.map((opt) => {
            const count = results?.options.find((o) => o.id === opt.id)?.votes ?? 0;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <button
                key={opt.id}
                onClick={() => votePoll(poll.id, opt.id)}
                className="relative block w-full overflow-hidden rounded-md bg-slate-700/70 px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-600"
              >
                <span
                  className="absolute inset-y-0 left-0 bg-indigo-500/30"
                  style={{ width: `${pct}%` }}
                />
                <span className="relative z-10 flex justify-between">
                  <span>{opt.text}</span>
                  <span className="text-slate-300">{count}</span>
                </span>
              </button>
            );
          })}
        </div>
        <div className="mt-1.5 text-[10px] text-slate-500">
          {results ? `${total} vote${total === 1 ? '' : 's'}` : 'No votes yet'}
          {poll.poll?.allowMultiple ? ' · multiple selection' : ''}
        </div>
        {canModerate && (
          <div className="mt-1 flex gap-2">
            <button onClick={() => moderate(poll.id, 'hide')} className="text-[10px] text-slate-400 hover:text-red-300">
              Hide
            </button>
          </div>
        )}
      </div>
    );
  });


  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-l border-slate-800 bg-slate-950 text-white">
      {/* Status */}
      <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2 text-[11px]">
        <span className="flex items-center gap-1.5 text-slate-300">
          <span
            className={`inline-block h-2 w-2 rounded-full ${
              room.status === 'open'
                ? 'bg-emerald-400'
                : room.status === 'degraded'
                  ? 'bg-amber-400'
                  : 'bg-slate-500'
            }`}
          />
          {statusLabel}
          {room.throttled && <span className="text-slate-500">· throttled</span>}
        </span>
        {room.degraded && (
          <span className="flex items-center gap-1 text-amber-300">
            <WifiOff className="h-3 w-3" /> REST fallback
          </span>
        )}
      </div>

      {banner && (
        <div className="flex items-center gap-1.5 border-b border-red-900/40 bg-red-500/20 px-3 py-1.5 text-[11px] text-red-200">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {banner}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-slate-800 text-xs">
        {(
          [
            ['chat', MessageSquare, `Chat (${messages.length})`],
            ['qa', HelpCircle, `Q&A (${qaCount})`],
            ['polls', BarChart3, `Polls (${pollCount})`],
          ] as const
        ).map(([key, Icon, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex flex-1 items-center justify-center gap-1 px-2 py-2 transition ${
              tab === key ? 'border-b-2 border-indigo-400 text-white' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 space-y-2 overflow-y-auto p-2">
        {tab === 'chat' &&
          (chatRows.length > 0 ? (
            chatRows
          ) : (
            <p className="px-2 py-6 text-center text-xs text-slate-500">No messages yet — say hello!</p>
          ))}
        {tab === 'qa' &&
          (qaRows.length > 0 ? (
            qaRows
          ) : (
            <p className="px-2 py-6 text-center text-xs text-slate-500">No questions yet — ask away!</p>
          ))}
        {tab === 'polls' && (
          <>
            {pollCards.length > 0 ? (
              pollCards
            ) : (
              <p className="px-2 py-6 text-center text-xs text-slate-500">No polls yet.</p>
            )}
            {canModerate &&
              (showPollForm ? (
                <PollCreateForm onCancel={() => setShowPollForm(false)} onCreate={createPoll} />
              ) : (
                <button
                  onClick={() => setShowPollForm(true)}
                  className="w-full rounded-lg border border-dashed border-slate-700 px-3 py-2 text-xs text-slate-400 hover:border-indigo-400 hover:text-indigo-200"
                >
                  + Create poll
                </button>
              ))}
          </>
        )}
      </div>

      {/* Composer */}
      {(tab === 'chat' || tab === 'qa') && (
        <div className="border-t border-slate-800 p-2">
          <div className="flex items-end gap-2">
            <textarea
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendComposer();
                }
              }}
              rows={2}
              maxLength={WS_MAX_MESSAGE_LENGTH}
              placeholder={tab === 'qa' ? 'Ask a question…' : 'Type a message…'}
              className="flex-1 resize-none rounded-md bg-slate-800 px-2 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <button
              onClick={sendComposer}
              disabled={!composer.trim()}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-indigo-600 text-white disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-1 flex items-center justify-between text-[10px] text-slate-500">
            <span>
              {composer.length}/{WS_MAX_MESSAGE_LENGTH}
            </span>
            <button
              onClick={toggleRaiseHand}
              className={`flex items-center gap-1 rounded px-1.5 py-0.5 ${
                handRaised ? 'bg-amber-500/20 text-amber-300' : 'hover:text-slate-300'
              }`}
            >
              <Hand className="h-3 w-3" />
              {handRaised ? 'Hand raised' : 'Raise hand'}
            </button>
          </div>
        </div>
      )}

      {/* Presence */}
      <div className="border-t border-slate-800 px-3 py-2">
        <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-400">
          <Users className="h-3 w-3" /> {presenceCount} present
        </div>
        <ul className="mt-1 max-h-28 space-y-0.5 overflow-y-auto text-xs">
          {presence.length === 0 && <li className="text-slate-500">Waiting for participants…</li>}
          {presence.map((u) => (
            <li key={u.userId} className="flex items-center justify-between">
              <span className="flex min-w-0 items-center gap-1.5 text-slate-200">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                <span className="truncate">
                  {u.name}
                  {u.userId === currentUserId && <span className="text-slate-500"> (you)</span>}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1 text-[10px] text-slate-500">
                {u.role === 'host' && <span className="text-amber-300">Host</span>}
                {u.handRaised && <Hand className="h-3 w-3 text-amber-400" />}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ------------------------------ poll create form ------------------------------ */

const PollCreateForm: React.FC<{
  onCancel: () => void;
  onCreate: (question: string, options: string) => void;
}> = ({ onCancel, onCreate }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState('');
  return (
    <div className="space-y-2 rounded-lg border border-indigo-500/40 bg-slate-900 p-3">
      <p className="text-[11px] font-semibold text-slate-300">Create a poll</p>
      <input
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="Poll question"
        maxLength={WS_MAX_MESSAGE_LENGTH}
        className="w-full rounded-md bg-slate-800 px-2 py-1.5 text-sm text-white placeholder:text-slate-500"
      />
      <textarea
        value={options}
        onChange={(e) => setOptions(e.target.value)}
        rows={4}
        placeholder={'Option 1\nOption 2\nOption 3'}
        className="w-full resize-none rounded-md bg-slate-800 px-2 py-1.5 text-sm text-white placeholder:text-slate-500"
      />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="rounded-md px-2 py-1 text-xs text-slate-400 hover:text-slate-200">
          Cancel
        </button>
        <button
          onClick={() => onCreate(question, options)}
          className="rounded-md bg-indigo-600 px-3 py-1 text-xs text-white hover:bg-indigo-500"
        >
          Launch poll
        </button>
      </div>
    </div>
  );
};

